import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import './XYChart.scss'
import { XYChartProps } from './XYChart.types'
import { scaleLinear, scaleTime, scaleOrdinal } from '@visx/scale'
import { curveBasis } from '@visx/curve'
import { GridRows, GridColumns } from '@visx/grid'
import {
    AreaSeries,
    AreaStack,
    Axis,
    BarGroup,
    BarStack,
    BarSeries,
    LineSeries,
    XYChart,
    Tooltip,
    Grid,
    AnimatedLineSeries,
    AnimatedAreaSeries,
    buildChartTheme,
} from '@visx/xychart'
import { AreaClosed, Line, Bar } from '@visx/shape'
import { max, extent, bisector } from 'd3-array'
import theming from '../../theming'
import { LegendOrdinal, LegendItem, LegendLabel } from '@visx/legend'
import merge from 'lodash/merge'
import { useTooltip } from '@visx/tooltip'
import { LinearGradient } from '@visx/gradient'
import appleStock, { AppleStock } from '@visx/mock-data/lib/mocks/appleStock'
const legendGlyphSize = 20
const stock = appleStock.slice(800)

const defaultMargin = {
    top: 20,
    left: 20,
    right: 60,
    bottom: 50,
}

const ArcTradeXYChart: React.FC<XYChartProps> = ({
    margin = {},
    xAxisFormat = (d) => moment(d).format('MM/DD/YY'),
    valueDisplayFormat,
    type = 'StackedBar',
    width,
    height,
    data,
    xAxisKey,
    valueKeys,
    setLegend,
    tickFormat,
    ylabel,
    xlabel,
    ylabelOffset,
    xlabelOffset,
    xAxisScope,
    orientation,
    onDataItemClick,
    legendPosition,
    secondaryValueKeys = [],
    colorAccessor,
    tooltipRenderer,
}) => {
    const _margin = merge(defaultMargin, margin)
    const [selectedKeys, setSelectedKeys] = useState(
        valueKeys.map((v) => {
            return {
                ...v,
                show: true,
            }
        }),
    )
    const getDate = (d: AppleStock) => new Date(d.date)
    const getStockValue = (d: AppleStock) => d.close

    const [currentHoverData, setCurrentHoverData] = useState(null)
    const { tooltipData } = useTooltip()

    const accessors = {
        xAccessor: (d) => d.x,
        yAccessor: (d) => d.y,
    }

    const valueKeyColors = useMemo(() => {
        let colors = valueKeys
            .reduce((c, v) => {
                if (v.color) {
                    c.push(v.color)
                }
                return c
            }, [])
            .slice()

        if (colors.length === 0) {
            colors = theming.colors.slice()
        }
        // const c = []
        // const col = colors.slice()
        // selectedKeys.forEach((k) => {
        //     const m = col
        //     if (k.show) {
        //         const color = colors.shift()
        //         c.push(color)
        //     }
        // })
        return colors
    }, [selectedKeys, valueKeys])

    const customTheme = useMemo(() => {
        return buildChartTheme({
            gridColor: '',
            gridColorDark: '',
            tickLength: 0,
            ...theming,
            backgroundColor: '#fff', // used by Tooltip, Annotation
            colors: valueKeyColors, // categorical colors, mapped to series via `dataKey`s
        })
    }, [])
    const ordinalColorScale = scaleOrdinal({
        domain: valueKeys.map((v) => v.key),
        range: customTheme.colors,
    })

    const tickWidth = 90
    const tickCount = Math.floor(width / tickWidth)

    useEffect(() => {
        // @ts-ignore
        setLegend(
            <LegendOrdinal scale={ordinalColorScale} labelFormat={(label) => `${label?.toString().toUpperCase()}`}>
                {(labels) => (
                    <div className={'r-chart-legend-container'}>
                        {labels.reverse().map((label, i) => {
                            const isHidden = selectedKeys.find((k) => k.key === label.datum).show === false
                            return (
                                <LegendItem
                                    onClick={() => {
                                        setSelectedKeys((prev) => {
                                            const newVal = prev.slice().map((v) => {
                                                if (v.key === label.datum) {
                                                    v.show = !v.show
                                                }
                                                return v
                                            })
                                            return [...newVal]
                                        })
                                    }}
                                    key={`legend-quantile-${i}`}
                                    margin='5px 5px'
                                >
                                    <svg
                                        style={{ opacity: isHidden ? 0.5 : 1 }}
                                        width={legendGlyphSize}
                                        height={legendGlyphSize}
                                    >
                                        <rect fill={label.value} width={legendGlyphSize} height={legendGlyphSize} />
                                    </svg>
                                    <LegendLabel
                                        style={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            margin: '0 0 0 4px',
                                            opacity: isHidden ? 0.5 : 1,
                                        }}
                                        align='left'
                                    >
                                        {
                                            valueKeys.find((v) => {
                                                return v.key === label.datum
                                            })?.label
                                        }
                                    </LegendLabel>
                                </LegendItem>
                            )
                        })}
                    </div>
                )}
            </LegendOrdinal>,
        )
    }, [data, selectedKeys, valueKeys])

    useEffect(() => {
        setSelectedKeys(
            valueKeys.map((v) => {
                return {
                    ...v,
                    show: true,
                }
            }),
        )
    }, [valueKeys])

    const tooltipTitle = (datum) => {
        if (xAxisScope) {
            switch (xAxisScope) {
                case 'HOUR':
                    return moment(datum).format('MM/DD/YY HH:mm')
                case 'DAY':
                    return moment(datum).format('MM/DD/YY')
                case 'MONTH':
                    return moment(datum).format('MM/YY')
                case 'YEAR':
                    return moment(datum).format('YYYY')
            }
        }
        return datum
    }

    const formatDateTime = (datum) => {
        switch (xAxisScope) {
            case 'HOUR':
                return moment(datum).format('HH:mm')
            case 'DAY':
                return moment(datum).format('MM/DD/YY')
            case 'MONTH':
                return moment(datum).format('MM/YY')
            case 'YEAR':
                return moment(datum).format('YYYY')
        }
        return datum
    }

    const handleClick = useCallback(() => {
        // debugger
        if (onDataItemClick && currentHoverData) {
            onDataItemClick(currentHoverData)
        }
    }, [currentHoverData])

    useEffect(() => {
        document.addEventListener('click', handleClick)
        return () => {
            document.removeEventListener('click', handleClick)
        }
    }, [currentHoverData])

    // bounds
    const innerWidth = width - _margin.left - _margin.right
    const innerHeight = height - _margin.top - _margin.bottom

    const yScale = useCallback(
        (vKey) => {
            const x = max(data, (d) => d[vKey]) || 0
            const t = scaleLinear({
                range: [innerHeight + _margin.top, _margin.top],
                domain: [0, x + innerHeight / 3],
                nice: true,
            })
            return t
        },

        [_margin.top, innerHeight],
    )

    const xScale = useMemo(
        () =>
            scaleTime({
                range: [_margin.left, innerWidth + _margin.left],
                domain: extent(data, (d) => d[xAxisKey]) as [Date, Date],
            }),
        [innerWidth, _margin.left],
    )

    const background = '#3b6978'
    const background2 = '#204051'
    const accentColor = '#edffea'
    const accentColorDark = '#75daad'

    if (!data || data.length === 0) {
        return null
    }

    return (
        <div data-testid='XYChart' className='r-xychart-container'>
            <XYChart
                onPointerMove={(d) => {
                    if (type === 'BarGroup') {
                        setCurrentHoverData(d.datum)
                    } else if (type === 'BarChart') {
                        // @ts-ignore
                        setCurrentHoverData(d.datum)
                    } else {
                        // @ts-ignore
                        setCurrentHoverData(d.datum.data)
                    }
                }}
                onPointerOut={(d) => {
                    setCurrentHoverData(null)
                }}
                margin={_margin}
                height={height}
                width={width}
                xScale={orientation === 'horizontal' ? { type: 'linear' } : { type: 'band' }}
                yScale={orientation === 'horizontal' ? { type: 'band' } : { type: 'linear' }}
                theme={customTheme}
            >
                <LinearGradient id='area-background-gradient' from={background} to={background2} />
                <LinearGradient id='area-gradient' from={accentColor} to={accentColor} toOpacity={0.1} />

                <Grid columns={false} />
                <Axis
                    orientation={orientation === 'horizontal' ? 'left' : 'bottom'}
                    numTicks={orientation === 'horizontal' ? Math.min(8, Math.floor(height / 30)) : tickCount}
                    tickLength={10}
                    tickLabelProps={(d) => {
                        return { fontWeight: 600, color: 'red' }
                    }}
                    tickFormat={(d, i) => {
                        if (tickFormat) {
                            return tickFormat(d)
                        }
                        return formatDateTime(d)
                    }}
                    label={xlabel}
                    labelOffset={xlabelOffset}
                />
                <Axis
                    numTicks={orientation === 'horizontal' ? tickCount : Math.min(8, Math.floor(height / 30))}
                    label={ylabel}
                    labelOffset={ylabelOffset}
                    tickLabelProps={(d) => {
                        return { fontWeight: 600 }
                    }}
                    orientation={orientation === 'horizontal' ? 'bottom' : 'left'}
                />

                {selectedKeys.map((key, i) => {
                    if (key.chartType === 'Line' && key.show) {
                        return (
                            <LineSeries
                                dataKey={key.key}
                                data={data}
                                xAccessor={(d) => d[xAxisKey]}
                                yAccessor={(d) => d[key.key]}
                                {...key.chartProps}
                            />
                        )
                    }
                    return null
                })}

                {type === 'StackedArea' || type === 'LineChart' ? (
                    <AreaStack>
                        {selectedKeys.map((key, i) => {
                            if (!key.show || key.chartType) {
                                return null
                            }
                            return (
                                <AreaSeries
                                    key={i}
                                    opacity={0.5}
                                    lineProps={{ width: 1 }}
                                    strokeWidth={0.2}
                                    dataKey={key.key}
                                    data={data}
                                    xAccessor={(d) => d[xAxisKey]}
                                    yAccessor={(d) => d[key.key]}
                                />
                            )
                        })}
                    </AreaStack>
                ) : null}
                {type === 'AreaChart'
                    ? selectedKeys.map((key, i) => {
                          if (!key.show || key.chartType) {
                              return null
                          }
                          return (
                              <React.Fragment>
                                  <AnimatedLineSeries
                                      key={i}
                                      // barPadding={10}
                                      dataKey={key.key}
                                      data={data}
                                      strokeWidth={5}
                                      opacity={!key.show ? 0 : 1}
                                      curve={curveBasis}
                                      {...key.chartProps}
                                      xAccessor={(d) => d[xAxisKey]}
                                      yAccessor={(d) => d[key.key]}
                                  />
                                  <AnimatedAreaSeries
                                      key={i}
                                      opacity={!key.show ? 0 : 0.1}
                                      lineProps={{ width: 1 }}
                                      onPointerUp={(d) => {
                                          onDataItemClick(d)
                                      }}
                                      strokeWidth={0.2}
                                      dataKey={key.key}
                                      color={'green'}
                                      data={data}
                                      renderLine={false}
                                      curve={curveBasis}
                                      {...key.chartProps}
                                      xAccessor={(d) => d[xAxisKey]}
                                      yAccessor={(d) => d[key.key]}
                                  />
                              </React.Fragment>
                          )
                      })
                    : null}

                {type === 'StackedBar' ? (
                    <BarStack>
                        {selectedKeys.map((key, i) => {
                            if (!key.show) {
                                return null
                            }
                            return (
                                <BarSeries
                                    key={i}
                                    barPadding={0}
                                    dataKey={key.key}
                                    data={data}
                                    xAccessor={(d) => d[xAxisKey]}
                                    yAccessor={(d) => d[key.key]}
                                />
                            )
                        })}
                    </BarStack>
                ) : null}
                {type === 'BarGroup' || type === 'BarChart' ? (
                    <BarGroup>
                        {selectedKeys.map((key, i) => {
                            if (!key.show) {
                                return null
                            }
                            return (
                                <BarSeries
                                    colorAccessor={colorAccessor ? (d) => colorAccessor(d, key) : null}
                                    key={i}
                                    // barPadding={10}
                                    dataKey={key.key}
                                    data={data}
                                    xAccessor={(d) => {
                                        // return orientation === 'horizontal' ? d[xAxisKey] : d[xAxisKey]
                                        return orientation === 'horizontal' ? d[key.key] : d[xAxisKey]
                                    }}
                                    yAccessor={(d) => {
                                        return orientation === 'horizontal' ? d[xAxisKey] : d[key.key]
                                        // return orientation === 'horizontal' ? d[key.key] : d[key.key]
                                    }}
                                />
                            )
                        })}
                    </BarGroup>
                ) : null}
                {type === 'LineChart'
                    ? selectedKeys.map((key, i) => {
                          if (!key.show) {
                              return null
                          }
                          return (
                              <React.Fragment>
                                  <LineSeries
                                      key={i}
                                      // barPadding={10}
                                      dataKey={key.key}
                                      data={data}
                                      curve={curveBasis}
                                      xAccessor={(d) => d[xAxisKey]}
                                      yAccessor={(d) => d[key.key]}
                                  />

                                  <AreaClosed<any>
                                      data={data}
                                      x={(d) => {
                                          console.log('x', d[xAxisKey])
                                          return xScale(d[xAxisKey])
                                      }}
                                      y={(d) => {
                                          console.log('y', d[key.key])
                                          return d[key.key]
                                      }}
                                      yScale={yScale(key.key)}
                                      strokeWidth={1}
                                      stroke='url(#area-gradient)'
                                      fill='url(#area-gradient)'
                                      curve={curveBasis}
                                  />
                                  {/*<AreaSeries*/}
                                  {/*    key={i}*/}
                                  {/*    style={{ opacity: 0.5 }}*/}
                                  {/*    // barPadding={10}*/}
                                  {/*    dataKey={key.key}*/}
                                  {/*    data={data}*/}
                                  {/*    curve={curveBasis}*/}
                                  {/*    xAccessor={(d) => d[xAxisKey]}*/}
                                  {/*    yAccessor={(d) => d[key.key]}*/}
                                  {/*/>*/}
                              </React.Fragment>
                          )
                      })
                    : null}

                {secondaryValueKeys.map((c, i) => {
                    return (
                        <LineSeries
                            key={i}
                            strokeWidth={2}
                            dataKey={c.key}
                            data={data}
                            {...c.chartProps}
                            xAccessor={(d) => d[xAxisKey]}
                            yAccessor={(d) => d[c.key]}
                        />
                    )
                })}
                <Tooltip
                    verticalCrosshairStyle={{ stroke: 'lightgray', strokeDasharray: '8' }}
                    className={'r-chart-tooltip'}
                    snapTooltipToDatumX
                    snapTooltipToDatumY
                    showVerticalCrosshair
                    showHorizontalCrosshair
                    // showSeriesGlyphs
                    renderTooltip={({ tooltipData, colorScale }) => {
                        if (tooltipRenderer) {
                            return tooltipRenderer(tooltipData, colorScale)
                        }

                        const total = selectedKeys.reduce((total, key) => {
                            const val = tooltipData?.datumByKey[key.key]?.datum[key.key] || 0
                            return (total += val)
                        }, 0)

                        return (
                            <div style={{ padding: '10px' }}>
                                <div className={'r-tooltip-title'}>
                                    {tooltipTitle(tooltipData.nearestDatum.datum[xAxisKey])}
                                </div>
                                <table>
                                    {selectedKeys
                                        .slice()
                                        .reverse()
                                        .map((vk) => {
                                            if (!vk.show) {
                                                return null
                                            }
                                            const label = vk.label
                                            const value = valueDisplayFormat
                                                ? valueDisplayFormat(tooltipData?.datumByKey[vk.key]?.datum[vk.key])
                                                : tooltipData?.datumByKey[vk.key]?.datum[vk.key]

                                            return (
                                                <tr
                                                    className={
                                                        tooltipData.nearestDatum.key === vk.key
                                                            ? 'r-current-tooltip-item'
                                                            : ''
                                                    }
                                                >
                                                    <td>
                                                        <div
                                                            style={{
                                                                background: colorScale(vk.key),
                                                            }}
                                                            className={'r-tooltip-label-glyph'}
                                                        ></div>
                                                    </td>
                                                    <td>{label}</td>
                                                    {/*<th>{tooltipData[vk.key].data}</th>*/}
                                                    <td style={{ textAlign: 'right' }}>{value}</td>
                                                </tr>
                                            )
                                        })}
                                    <tr>
                                        <td></td>
                                        <td style={{ textAlign: 'right' }}>
                                            <i>Total</i>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {valueDisplayFormat ? valueDisplayFormat(total) : total}
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        )
                    }}
                />
            </XYChart>
        </div>
    )
}

export default ArcTradeXYChart
